// src/client/game/core/enemy/controllers/BossController.ts
import type { EnemyController } from "../EnemyController";
import type { EnemyContext } from "../EnemyContext";
import type { Direction, GridPos } from "../../types";

const DIRS: Direction[] = [
  { dx: 0, dy: -1 },
  { dx: 1, dy: 0 },
  { dx: 0, dy: 1 },
  { dx: -1, dy: 0 },
];

export type BossPhase = 1 | 2;

/**
 * Boss controller with two simple phases:
 * - Phase 1: "stalk" (slightly noisy chase)
 * - Phase 2: "enraged" (more direct, tries to cut off by aiming deeper into the body)
 */
export class BossController implements EnemyController {
  readonly id = "boss";

  private phase: BossPhase = 1;

  setPhase(phase: BossPhase): void {
    this.phase = phase;
  }

  decideNextMove(ctx: EnemyContext): Direction {
    const head = ctx.self.snake.segments[0];
    const player = ctx.world.player;

    const target = this.pickTarget(player.segments);

    const candidates = DIRS
      .map((d) => ({ d, p: { x: head.x + d.dx, y: head.y + d.dy } as GridPos }))
      .filter(({ p }) => !ctx.world.isBlocked(p));

    if (candidates.length === 0) return ctx.self.snake.direction;

    // Add a touch of randomness in phase 1 so it feels less "robotic".
    const noise = this.phase === 1 ? 0.35 : 0.08;

    const scored = candidates
      .map(({ d, p }) => ({
        d,
        score: this.dist(p, target) + (ctx.rng() - 0.5) * noise,
      }))
      .sort((a, b) => a.score - b.score);

    return scored[0]?.d ?? ctx.self.snake.direction;
  }

  private pickTarget(playerSegs: GridPos[]): GridPos {
    // Phase 2 tries to "cut" the snake by aiming at a deeper segment.
    const idx = this.phase === 2 ? Math.min(4, Math.max(0, playerSegs.length - 1)) : 0;
    return playerSegs[idx] ?? playerSegs[0]!;
  }

  private dist(a: GridPos, b: GridPos): number {
    return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
  }
}
